/**
 * CVRequests — combined inbox (teacher view) + outbox (recruiter view).
 * Single page; user toggles between Incoming and Outgoing tabs.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/Skeleton";
import { Inbox, Send, Check, X, Mail, Loader2, Clock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const STATUS_CHIP = {
  pending:  "tb-chip-butter",
  approved: "tb-chip-mint",
  rejected: "tb-chip-rose",
  expired:  "tb-chip-sky",
};

export default function CVRequests() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("incoming");
  const [incoming, setIncoming] = useState(null);
  const [outgoing, setOutgoing] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/marketplace/cv-requests/incoming").then((r) => r.data).catch(() => []),
      api.get("/marketplace/cv-requests/outgoing").then((r) => r.data).catch(() => []),
    ]).then(([i, o]) => { setIncoming(i); setOutgoing(o); });
  }, []);

  const respond = async (req_id, action) => {
    try {
      await api.post(`/marketplace/cv-request/${req_id}/respond`, { action });
      setIncoming((curr) => curr.map((r) => r.request_id === req_id ? { ...r, status: action === "approve" ? "approved" : "rejected" } : r));
      toast.success(`Request ${action}d`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    }
  };

  const rows = tab === "incoming" ? incoming : outgoing;

  return (
    <Layout title="CV Access Requests">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="rounded-full mb-3" data-testid="cv-back">
        <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back
      </Button>

      <div className="mb-5">
        <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
          <Inbox className="h-3 w-3" /> TeeBee CV gating
        </span>
        <h1 className="mt-3 font-display text-3xl tracking-tight font-medium leading-tight">CV access requests</h1>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-xl">
          Privacy-first: every request needs your explicit approval before a recruiter can view your full CV.
        </p>
      </div>

      <div className="inline-flex rounded-full border border-border p-1 mb-4" data-testid="cv-tabs">
        <button
          className={`text-xs px-3 py-1.5 rounded-full transition-all ${tab === "incoming" ? "bg-primary text-primary-foreground" : ""}`}
          onClick={() => setTab("incoming")}
          data-testid="cv-tab-incoming"
        >
          <Inbox className="h-3 w-3 mr-1 inline" /> Incoming ({incoming?.length || 0})
        </button>
        <button
          className={`text-xs px-3 py-1.5 rounded-full transition-all ${tab === "outgoing" ? "bg-primary text-primary-foreground" : ""}`}
          onClick={() => setTab("outgoing")}
          data-testid="cv-tab-outgoing"
        >
          <Send className="h-3 w-3 mr-1 inline" /> Outgoing ({outgoing?.length || 0})
        </button>
      </div>

      {!rows ? (
        <div className="space-y-2"><Skeleton className="h-20" /><Skeleton className="h-20" /></div>
      ) : rows.length === 0 ? (
        <div className="tb-card p-10 text-center" data-testid="cv-empty">
          <span className="mx-auto h-14 w-14 grid place-items-center rounded-full border border-dashed border-border mb-3">
            <Mail className="h-6 w-6 text-muted-foreground" />
          </span>
          <p className="font-display text-lg font-medium">
            {tab === "incoming" ? "No requests yet" : "You haven't requested any CVs"}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {tab === "incoming"
              ? "Recruiters who want your CV will appear here for your approval."
              : "Visit Discover Educators to request access to a teacher's CV."}
          </p>
        </div>
      ) : (
        <div className="space-y-3 tb-stagger">
          {rows.map((r) => (
            <div key={r.request_id} className="tb-card p-4 flex items-start gap-3" data-testid={`cv-row-${r.request_id}`}>
              <span className="h-9 w-9 rounded-xl tb-chip-lavender grid place-items-center">
                {tab === "incoming" ? <Inbox className="h-4 w-4" /> : <Send className="h-4 w-4" />}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">
                  {tab === "incoming"
                    ? <>{r.recruiter_name || "Recruiter"} <span className="text-muted-foreground font-normal">from {r.recruiter_org || "an institution"}</span></>
                    : <>{r.teacher_name || "Educator"}</>}
                </p>
                {r.message && <p className="text-[12px] text-muted-foreground mt-0.5 italic">&ldquo;{r.message}&rdquo;</p>}
                <p className="text-[11px] text-muted-foreground mt-1 inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {new Date(r.created_at).toLocaleString()}
                </p>
              </div>
              <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-full ${STATUS_CHIP[r.status] || "tb-chip-sky"}`}>
                {r.status}
              </span>
              {tab === "incoming" && r.status === "pending" && (
                <div className="flex flex-col gap-1.5 ml-2">
                  <Button size="sm" className="rounded-full" onClick={() => respond(r.request_id, "approve")} data-testid={`cv-approve-${r.request_id}`}>
                    <Check className="h-3 w-3 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => respond(r.request_id, "reject")} data-testid={`cv-reject-${r.request_id}`}>
                    <X className="h-3 w-3 mr-1" /> Decline
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
